import type { CaptureResult } from "posthog-js"

/**
 * Browsers report a ResizeObserver delivery loop through `window.onerror` with
 * no `error` object attached, so posthog-js can only build a synthetic
 * exception: no stack, no frames, nothing naming the observer that looped. The
 * error is therefore unattributable by design, and it lands in error tracking
 * as a bare message.
 *
 * This module records the missing half itself. It wraps the global constructor
 * so every observer remembers who created it and what it watches, and counts
 * how many callbacks the browser delivered inside the current frame (a loop
 * shows up as an unusually high count). `enrichExceptionEvent` then attaches
 * that snapshot to the exception on its way out, so the next occurrence arrives
 * with a culprit list instead of a lone sentence.
 *
 * It diagnoses only: no event is ever dropped or altered otherwise.
 */

export const RESIZE_OBSERVER_DIAGNOSTICS_LIMITS = {
  /** Observers kept in the ring buffer, newest wins. */
  maxTrackedObservers: 8,
  /** Element descriptors kept per observer. */
  maxTargetsPerObserver: 4,
  /** Characters kept of the creation stack. */
  maxStackChars: 300,
  /** Characters kept of a single element descriptor. */
  maxTargetChars: 80,
  /**
   * Callbacks delivered this close together belong to the same delivery pass.
   * A frame is ~16 ms at 60 Hz; the window is generous so a slow frame does not
   * read as two.
   */
  frameWindowMs: 50,
} as const

/** Stack frames belonging to this module: the capture helper and the wrapper. */
const INTERNAL_STACK_FRAMES = 2
/** Frames kept of the caller, enough to name the hook or library. */
const MAX_STACK_FRAMES = 3
/** Reported when no callback has run yet. */
const NO_CALLBACK_YET = -1

const LOOP_MESSAGE_PATTERNS = [
  "resizeobserver loop completed with undelivered notifications",
  "resizeobserver loop limit exceeded",
] as const

const EXCEPTION_EVENT = "$exception"

type ObserverRecord = {
  readonly id: number
  readonly creationSite: string
  readonly targets: readonly string[]
  readonly callbackRuns: number
}

type DiagnosticsState = {
  readonly records: readonly ObserverRecord[]
  readonly nextObserverId: number
  readonly frameCallbacks: number
  readonly lastCallbackAt: number | null
}

const EMPTY_STATE: DiagnosticsState = {
  records: [],
  nextObserverId: 1,
  frameCallbacks: 0,
  lastCallbackAt: null,
}

// Module-level because the wrapper has to answer from inside a browser error
// handler, which carries no context of its own.
let state: DiagnosticsState = EMPTY_STATE

function truncate(value: string, maxChars: number): string {
  return value.length > maxChars ? `${value.slice(0, maxChars)}…` : value
}

/**
 * Names the code that built the observer. Stack shapes differ per engine
 * (`    at fn (file)` on V8, `fn@file` on WebKit), so frames are picked by
 * matching either form rather than by dropping a fixed number of leading lines.
 */
function captureCreationSite(): string {
  const stack = new Error().stack
  if (!stack) return "unknown"

  const frames = stack
    .split("\n")
    .filter((line) => /^\s*(at\s|.+@)/.test(line))
    .slice(INTERNAL_STACK_FRAMES, INTERNAL_STACK_FRAMES + MAX_STACK_FRAMES)
    .map((line) => line.trim())

  if (frames.length === 0) return "unknown"
  return truncate(
    frames.join(" <- "),
    RESIZE_OBSERVER_DIAGNOSTICS_LIMITS.maxStackChars,
  )
}

/**
 * `tag#id.class[data-slot=…]`, read through `getAttribute` so an SVG element
 * (whose `className` is an object, not a string) describes just as well.
 */
function describeElement(target: Element): string {
  const tag = target.tagName ? target.tagName.toLowerCase() : "unknown"
  const id = target.id ? `#${target.id}` : ""
  const classAttribute = target.getAttribute("class")?.trim()
  const classes = classAttribute
    ? `.${classAttribute.split(/\s+/).join(".")}`
    : ""
  const slot = target.getAttribute("data-slot")
  const slotSuffix = slot ? `[data-slot=${slot}]` : ""

  return truncate(
    `${tag}${id}${classes}${slotSuffix}`,
    RESIZE_OBSERVER_DIAGNOSTICS_LIMITS.maxTargetChars,
  )
}

function registerObserver(creationSite: string): number {
  const id = state.nextObserverId
  const records = [
    ...state.records,
    { id, creationSite, targets: [], callbackRuns: 0 },
  ].slice(-RESIZE_OBSERVER_DIAGNOSTICS_LIMITS.maxTrackedObservers)

  state = { ...state, records, nextObserverId: id + 1 }
  return id
}

function updateRecord(
  id: number,
  update: (record: ObserverRecord) => ObserverRecord,
): void {
  state = {
    ...state,
    records: state.records.map((record) =>
      record.id === id ? update(record) : record,
    ),
  }
}

function recordTarget(id: number, descriptor: string): void {
  updateRecord(id, (record) =>
    record.targets.length >=
    RESIZE_OBSERVER_DIAGNOSTICS_LIMITS.maxTargetsPerObserver
      ? record
      : { ...record, targets: [...record.targets, descriptor] },
  )
}

function recordCallbackRun(id: number): void {
  const now = Date.now()
  const sameFrame =
    state.lastCallbackAt !== null &&
    now - state.lastCallbackAt <=
      RESIZE_OBSERVER_DIAGNOSTICS_LIMITS.frameWindowMs

  state = {
    ...state,
    frameCallbacks: sameFrame ? state.frameCallbacks + 1 : 1,
    lastCallbackAt: now,
  }
  updateRecord(id, (record) => ({
    ...record,
    callbackRuns: record.callbackRuns + 1,
  }))
}

export type ResizeObserverHost = {
  ResizeObserver?: typeof ResizeObserver
}

/**
 * Wraps `host.ResizeObserver`. Install it before the page builds its observers
 * (during hydration, not on an idle callback) or the ones already created stay
 * invisible to the diagnostics. Returns the uninstall function, which restores
 * the native constructor and clears what was recorded.
 */
export function installResizeObserverDiagnostics(
  host: ResizeObserverHost,
): () => void {
  const NativeResizeObserver = host.ResizeObserver
  // Server render, or a browser old enough to lack the API: nothing to observe.
  if (!NativeResizeObserver) {
    return () => {
      state = EMPTY_STATE
    }
  }

  class DiagnosticResizeObserver extends NativeResizeObserver {
    readonly #observerId: number

    constructor(callback: ResizeObserverCallback) {
      let observerId = 0
      super((entries, observer) => {
        recordCallbackRun(observerId)
        callback(entries, observer)
      })
      observerId = registerObserver(captureCreationSite())
      this.#observerId = observerId
    }

    override observe(target: Element, options?: ResizeObserverOptions): void {
      recordTarget(this.#observerId, describeElement(target))
      super.observe(target, options)
    }
  }

  host.ResizeObserver = DiagnosticResizeObserver

  return () => {
    host.ResizeObserver = NativeResizeObserver
    state = EMPTY_STATE
  }
}

export function isResizeObserverLoopError(message: string): boolean {
  const normalized = message.toLowerCase()
  return LOOP_MESSAGE_PATTERNS.some((pattern) => normalized.includes(pattern))
}

/** Flat properties, because PostHog event properties are not nested. */
export function buildResizeObserverLoopContext(): Record<
  string,
  string | number
> {
  const observers = state.records
    .map(
      (record) =>
        `#${record.id} runs=${record.callbackRuns} targets=${record.targets.join("|")} from=${record.creationSite}`,
    )
    .join("\n")

  return {
    ro_frame_callbacks: state.frameCallbacks,
    ro_ms_since_last_callback:
      state.lastCallbackAt === null
        ? NO_CALLBACK_YET
        : Date.now() - state.lastCallbackAt,
    ro_observers: observers,
  }
}

type CapturedException = {
  readonly value?: string
}

/**
 * posthog-js `before_send`: annotates a ResizeObserver loop exception with the
 * recorded observers and passes everything else through untouched.
 */
export function enrichExceptionEvent(
  event: CaptureResult | null,
): CaptureResult | null {
  if (!event || event.event !== EXCEPTION_EVENT) return event

  const exceptions = event.properties.$exception_list as
    | readonly CapturedException[]
    | undefined
  const values = event.properties.$exception_values as
    | readonly string[]
    | undefined
  const message = exceptions?.[0]?.value ?? values?.[0] ?? ""

  if (!isResizeObserverLoopError(message)) return event

  return {
    ...event,
    properties: { ...event.properties, ...buildResizeObserverLoopContext() },
  }
}
